import { produce } from "immer";
import { QueryKey } from "@tanstack/react-query";

import {
  OptimisticMutationUpdateOptions,
  useOptimisticMutation,
} from "./useOptimisticMutation";

interface UseToggleOptions<T> {
  queryKey: QueryKey;
  addMutationFn: (entity: T | any) => any;
  deleteMutationFn: (entity: T | any) => any;
  getSelectionKey: (item: T) => number;
  updateOptions: OptimisticMutationUpdateOptions;
}

export interface UseToggleContext<T = any> {
  add: (entity: T) => void;
  remove: (entityId: number) => void;
  isPending: boolean;
  isSuccess: boolean;
  reset: () => void;
}

export function useToggleMutation<T>({
  queryKey,
  addMutationFn,
  deleteMutationFn,
  getSelectionKey,
  updateOptions,
}: UseToggleOptions<T>): UseToggleContext<T> {
  const addMutation = useOptimisticMutation<T, T[]>({
    queryKey,
    updateOptions,
    mutationFn: addMutationFn,
    optimisticUpdate: (entity, oldData) => {
      if (!oldData) return [entity];
      return produce(oldData, (draft) => {
        draft.push(entity as any);
      });
    },
  });

  const deleteMutation = useOptimisticMutation<number, T[]>({
    queryKey,
    updateOptions,
    mutationFn: deleteMutationFn,
    optimisticUpdate: (entityId, oldData) => {
      if (!oldData) return [];
      return produce(oldData, (draft) => {
        return draft.filter((item: any) => getSelectionKey(item) !== entityId);
      });
    },
  });

  return {
    add: addMutation.mutate,
    remove: deleteMutation.mutate,
    isPending: addMutation.isPending || deleteMutation.isPending,
    isSuccess: addMutation.isSuccess || deleteMutation.isSuccess,
    reset: () => {
      addMutation.reset();
      deleteMutation.reset();
    },
  };
}
